import React, { useEffect, useState, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import { Navbar } from '@/shared/ui/Navbar';
import Footer from '@/shared/ui/Footer';
import ScrollToTopButton from '@/shared/ui/ScrollToTopButton';
import WhatsAppButton from '@/shared/ui/WhatsAppButton';
import Hero from '@/features/landing/components/Hero';
import { getImageUrl } from '@/shared/utils/imageUrl';
import { apiClient } from '@/shared/api/client';
import { ToastProvider, useToast } from '@/shared/ui/Toast';
import { useNetworkStatus } from '@/shared/hooks/useNetworkStatus';
import { WifiOff, Sparkles } from 'lucide-react';
import { useAppStore } from '@/shared/store/appStore';

// Lazy-loaded routes for code-splitting and performance
const StoreRouter = lazy(() => import('@/features/store/components/StoreRouter'));
const ContactSpecialistPage = lazy(() => import('@/features/landing/components/ContactSpecialistPage'));
const KnowledgeList = lazy(() => import('@/features/knowledge/components/KnowledgeList'));
const KnowledgeDetail = lazy(() => import('@/features/knowledge/components/KnowledgeDetail'));
const SchedulerPage = lazy(() => import('@/features/scheduler/components/SchedulerPage'));
const CityDetail = lazy(() => import('@/features/landing/components/CityDetail'));
const NewsletterConfirmation = lazy(() => import('@/features/landing/components/NewsletterConfirmation'));
const LegalPageView = lazy(() => import('@/features/landing/components/LegalPageView'));

// Lazy-loaded below-the-fold landing sections & overlays
const TrustSection = lazy(() => import('@/features/landing/components/TrustSection'));
const FAQSection = lazy(() => import('@/features/landing/components/FAQSection'));
const ContactForm = lazy(() => import('@/features/landing/components/ContactForm'));
const AboutUsSection = lazy(() => import('@/features/landing/components/AboutUsSection'));
const MilestoneSection = lazy(() => import('@/features/landing/components/MilestoneSection'));
const LeadPopup = lazy(() => import('@/features/landing/components/LeadPopup'));
const DopCineTapeScroll = lazy(() => import('@/features/dop/components/DopCineTapeScroll'));
const FilmPosterTape = lazy(() => import('@/features/movies/components/FilmPosterTape'));
const ProductionHousesMarquee = lazy(() => import('@/features/movies/components/ProductionHousesMarquee'));
const CinemaLabs = lazy(() => import('@/features/scheduler/components/CinemaLabs'));
const CompareOverlay = lazy(() => import('@/features/rigs/components/CompareOverlay'));

function RouteFallback() {
  return (
    <div className="w-full min-h-[60vh] max-w-7xl mx-auto px-4 py-16 animate-pulse flex flex-col items-center justify-center">
      <div className="w-12 h-12 rounded-full border-3 border-[#793bfb]/20 border-t-[#793bfb] animate-spin mb-4" />
      <div className="h-4 bg-zinc-800/40 rounded w-48 mb-2" />
    </div>
  );
}

function ScrollToAnchor() {
  const location = useLocation();

  useEffect(() => {
    if (location.state?.scrollTo) {
      const anchorId = location.state.scrollTo;
      
      // Remove scrollTo state to prevent loop scrolling on back actions
      window.history.replaceState({}, document.title);

      setTimeout(() => {
        const el = document.getElementById(anchorId);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth' });
        }
      }, 150);
    } else {
      // Standard navigation: reset page scroll position to the top
      window.scrollTo(0, 0);
    }
  }, [location.pathname]);

  return null;
}

const DEFAULT_SUBHEADINGS = {
  "Atlas Lens Co.": "Anamorphic Optics",
  "Betz-Tools GmbH": "Rig Stabilization",
  "BLKBIRD": "Custom Cages",
  "Cinema Devices": "Body Support",
  "Dynacore": "Li-ion Batteries",
  "Eartec": "Crew Intercoms",
  "Egripment": "Dolly & Cranes",
  "Feelworld": "On-Camera Monitors",
  "FloatCam": "Linear Sliders",
  "Flowcine": "Gimbal Arm Postures",
  "Freefly Systems": "MōVI Gimbals",
  "HD Air Studio": "Gimbal Designs",
  "iFootage": "Monopods & Jibs",
  "MotoCrane": "Tracking Vehicles",
  "NB Stabilizer": "Gimbal Rigs",
  "Panther": "Dolly Systems",
  "proton": "Camera Controls",
  "SmallHD": "Production Monitors",
  "SmartSystem": "Linear Sliders",
  "Tilta": "Camera Rigs & Cages",
  "Steadicam – The Tiffen Company": "Body Stabilizers",
  "Transvideo": "Wireless Monitors",
  "Walter Klassen FX": "Vehicle Mounts",
  "Zacuto": "EVF & Rigging"
};

function Home() {
  const navigate = useNavigate();
  const apiBaseUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000';
  const [logoErrors, setLogoErrors] = useState({});
  const [brands, setBrands] = useState([]);
  const [loadingBrands, setLoadingBrands] = useState(true);

  const [scrollSequences, setScrollSequences] = useState([]);

  useEffect(() => {
    apiClient.get('/api/v1/landing/scroll-sequences')
      .then(res => {
        if (res.data && res.data.length > 0) {
          setScrollSequences(res.data);
        }
      })
      .catch(err => {
        console.warn("Failed to fetch scroll sequences from API:", err);
      });
  }, []);

  useEffect(() => {
    setLoadingBrands(true);
    apiClient.get('/api/v1/products/brands?format=objects&status=publish')
      .then(res => {
        if (res.data) {
          if (Array.isArray(res.data.items)) {
            setBrands(res.data.items);
          } else if (Array.isArray(res.data)) {
            setBrands(res.data);
          }
        }
      })
      .catch(err => {
        console.warn("Failed to fetch brands from API:", err);
      })
      .finally(() => {
        setLoadingBrands(false);
      });
  }, []);

  const handleBrandClick = (brand) => {
    if (!brand) {
      navigate('/shop');
    } else {
      const slug = typeof brand === 'string'
        ? brand.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
        : brand.slug;
      navigate(`/shop/brand/${slug}`);
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const seq1Config = scrollSequences.find(s => s.section_id === 'scroll-sec-1') || {};
  const seq2Config = scrollSequences.find(s => s.section_id === 'scroll-sec-2') || {};

  return (
    <div className="w-full flex flex-col items-center bg-[#F4F7F9]">
      {/* WebGL Animated Hero Section */}
      <Hero />

      {/* Authorized Distributor Network Section */}
      <section id="brands-anchor" className="w-full py-24 bg-gradient-to-r from-[#2b2344] via-[#1a152e] to-[#2b2344] border-y border-white/10 relative overflow-hidden">
        {/* Header Block (Centered grid alignment) */}
        <div className="max-w-7xl mx-auto px-6 mb-12 relative z-20">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 pb-8 border-b border-white/20">
            <div className="space-y-3">
              <h2 className="font-display text-3xl sm:text-4xl md:text-5xl text-white uppercase font-black tracking-tight">
                Authorized Distributor <span className="text-[#793bfb]">Network</span>
              </h2>
              <p className="text-white/70 max-w-2xl text-sm leading-relaxed font-sans">
                Cinescope India is the official certified distributor and service partner for global cinema hardware leaders. Browse authentic stock with manufacturer warranty support.
              </p>
            </div>
            
            <button 
              onClick={() => handleBrandClick('')}
              className="text-[11px] font-mono font-bold text-white hover:text-white uppercase tracking-wider border border-white/30 hover:bg-[#793bfb] bg-white/5 px-5 py-3 rounded-xl transition-all duration-300 shadow-sm self-start lg:self-end"
            >
              Explore Brands
            </button>
          </div>
        </div>

        {/* Infinite scrolling marquee of brand cards (Full width for premium immersive feel) */}
        <div className="w-full overflow-hidden py-4 select-none relative z-20">
          {/* Soft edge-fading overlays at screen boundaries */}
          <div className="absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-[#2b2344] to-transparent z-10 pointer-events-none" />
          <div className="absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-[#2b2344] to-transparent z-10 pointer-events-none" />

          {loadingBrands ? (
            <div className="flex gap-6 animate-pulse px-6">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="w-[280px] md:w-[310px] h-[260px] md:h-[280px] rounded-2xl bg-white/5 border border-white/10 shrink-0" />
              ))}
            </div>
          ) : brands.length > 0 ? (
            <div className="flex gap-6 animate-marquee-slow whitespace-nowrap min-w-max hover:pause">
            {/* Set 1 */}
            {brands.map((brandObj, idx) => {
              const name = typeof brandObj === 'string' ? brandObj : brandObj.name;
              const brandMetadata = {
                "Atlas Lens Co.": { category: "OPTICS", desc: "Premium anamorphic prime and zoom optics for cinematographers.", tag: "Sole Indian Dealer" },
                "Freefly Systems": { category: "STABILIZATION", desc: "Industry-standard MōVI stabilizers and heavy-lift cinema drones.", tag: "Authorized Partner" },
                "Tilta": { category: "RIGGING", desc: "Professional camera cages, follow focus systems, and power distribution setups.", tag: "Direct Distributor" },
                "Eartec": { category: "WIRELESS COMS", desc: "Self-contained full-duplex wireless crew intercom packages.", tag: "Exclusive Sales" },
                "Flowcine": { category: "BODY SUPPORT", desc: "Black Arm, xARM, and body-worn stabilizer posture rigs.", tag: "Direct Importer" },
                "SmallHD": { category: "MONITORS", desc: "High-accuracy, daylight-viewable production monitors.", tag: "Authorized Repair Site" },
                "Dynacore": { category: "POWER", desc: "Professional high-load Li-ion camera batteries and chargers.", tag: "Official Supplier" },
                "SmartSystem": { category: "MOTION SYSTEMS", desc: "High-precision linear sliders and motorized camera movement controls.", tag: "Direct Partner" }
              };

              const data = brandMetadata[name] || { 
                category: "EQUIPMENT", 
                desc: brandObj.description || `Explore premium production gear from ${name} at Cinescope India.`, 
                tag: "Certified Stockist" 
              };

              const subheadingText = brandObj.subheading || DEFAULT_SUBHEADINGS[name] || brandObj.description || data.desc;

              const logoUrl = brandObj.logo_path ? getImageUrl(brandObj.logo_path) : null;

              return (
                <div 
                  key={`brand-card-1-${idx}`}
                  onClick={() => handleBrandClick(brandObj)}
                  className="group inline-flex flex-col justify-between w-[280px] md:w-[310px] bg-gradient-to-b from-white/5 to-white/[0.02] hover:from-[#793bfb]/15 hover:to-transparent border border-white/10 hover:border-[#793bfb]/40 rounded-2xl p-6 transition-all duration-500 shadow-lg hover:shadow-[0_15px_40px_rgba(121,59,251,0.25)] hover:-translate-y-1.5 cursor-pointer h-[260px] md:h-[280px] relative overflow-hidden whitespace-normal text-white"
                >
                  <div className="absolute right-0 bottom-0 w-24 h-24 border-r border-b border-white/5 group-hover:border-white/10 transition-all pointer-events-none rounded-br-2xl" />
                  
                  <div className="space-y-4 text-left w-full">
                    {/* Category & Tag Row */}
                    <div className="flex justify-between items-center text-[9px] font-mono font-bold tracking-wider w-full">
                      <span className="bg-[#793bfb]/20 text-[#c0a2ff] border border-[#793bfb]/30 px-2 py-0.5 rounded uppercase group-hover:bg-[#793bfb] group-hover:text-white transition-all duration-300">
                        Equipment
                      </span>
                      <span className="text-white/40 group-hover:text-white/60 transition-all font-mono text-[9px] uppercase tracking-wider">
                        Authorised Distributor
                      </span>
                    </div>

                    {/* Brand Logo Presentation Box */}
                    <div className="relative w-full h-24 bg-black backdrop-blur-xs rounded-xl shadow-inner border border-white/10 p-0 flex items-center justify-center transition-transform duration-500 group-hover:scale-[1.02] overflow-hidden">
                      {logoUrl && !logoErrors[brandObj.id] ? (
                        <img 
                          src={logoUrl} 
                          alt={name} 
                          loading="lazy"
                          decoding="async"
                          onError={() => setLogoErrors(prev => ({ ...prev, [brandObj.id]: true }))}
                          className="h-full w-full object-contain filter drop-shadow-xs transition-transform duration-500"
                        />
                      ) : (
                        <div className="flex flex-col items-center justify-center text-zinc-500 p-3 bg-zinc-50/50 w-full h-full select-none">
                          <Sparkles className="w-6 h-6 text-[#793bfb]/60 mb-1 animate-pulse" />
                          <span className="font-mono text-[8px] text-[#793bfb] font-black uppercase tracking-widest leading-none">CINESCOPE</span>
                          <span className="font-display text-[10px] text-zinc-800 font-extrabold uppercase tracking-tight mt-0.5">{name}</span>
                        </div>
                      )}
                      
                      {/* Subtle overlay gloss highlight */}
                      <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-out pointer-events-none" />
                    </div>

                    {/* Name and subheading */}
                    <div className="space-y-1">
                      <h3 className="font-display text-base font-black text-white group-hover:text-[#c0a2ff] transition-all duration-300 uppercase tracking-wide leading-none">
                        {name}
                      </h3>
                      <p className="font-sans text-xs font-semibold text-white/70 group-hover:text-white transition-all line-clamp-1">
                        {subheadingText}
                      </p>
                    </div>
                  </div>

                  {/* Footer Arrow */}
                  <div className="pt-2 border-t border-white/5 flex items-center justify-between font-mono text-[9px] tracking-wider text-white/30 group-hover:text-[#c0a2ff] transition-all duration-300">
                    <span>EXPLORE SYSTEM</span>
                    <span className="transform group-hover:translate-x-1.5 transition-transform duration-300">↗</span>
                  </div>
                </div>
              );
            })}

            {/* Set 2 (for loop stability) */}
            {brands.map((brandObj, idx) => {
              const name = typeof brandObj === 'string' ? brandObj : brandObj.name;
              const brandMetadata = {
                "Atlas Lens Co.": { category: "OPTICS", desc: "Premium anamorphic prime and zoom optics for cinematographers.", tag: "Sole Indian Dealer" },
                "Freefly Systems": { category: "STABILIZATION", desc: "Industry-standard MōVI stabilizers and heavy-lift cinema drones.", tag: "Authorized Partner" },
                "Tilta": { category: "RIGGING", desc: "Professional camera cages, follow focus systems, and power distribution setups.", tag: "Direct Distributor" },
                "Eartec": { category: "WIRELESS COMS", desc: "Self-contained full-duplex wireless crew intercom packages.", tag: "Exclusive Sales" },
                "Flowcine": { category: "BODY SUPPORT", desc: "Black Arm, xARM, and body-worn stabilizer posture rigs.", tag: "Direct Importer" },
                "SmallHD": { category: "MONITORS", desc: "High-accuracy, daylight-viewable production monitors.", tag: "Authorized Repair Site" },
                "Dynacore": { category: "POWER", desc: "Professional high-load Li-ion camera batteries and chargers.", tag: "Official Supplier" },
                "SmartSystem": { category: "MOTION SYSTEMS", desc: "High-precision linear sliders and motorized camera movement controls.", tag: "Direct Partner" }
              };

              const data = brandMetadata[name] || { 
                category: "EQUIPMENT", 
                desc: brandObj.description || `Explore premium production gear from ${name} at Cinescope India.`, 
                tag: "Certified Stockist" 
              };

              const subheadingText2 = brandObj.subheading || DEFAULT_SUBHEADINGS[name] || brandObj.description || data.desc;

              const logoUrl = brandObj.logo_path ? getImageUrl(brandObj.logo_path) : null;

              return (
                <div 
                  key={`brand-card-2-${idx}`}
                  onClick={() => handleBrandClick(brandObj)}
                  className="group inline-flex flex-col justify-between w-[280px] md:w-[310px] bg-gradient-to-b from-white/5 to-white/[0.02] hover:from-[#793bfb]/15 hover:to-transparent border border-white/10 hover:border-[#793bfb]/40 rounded-2xl p-6 transition-all duration-500 shadow-lg hover:shadow-[0_15px_40px_rgba(121,59,251,0.25)] hover:-translate-y-1.5 cursor-pointer h-[260px] md:h-[280px] relative overflow-hidden whitespace-normal text-white"
                >
                  <div className="absolute right-0 bottom-0 w-24 h-24 border-r border-b border-white/5 group-hover:border-white/10 transition-all pointer-events-none rounded-br-2xl" />
                  
                  <div className="space-y-4 text-left w-full">
                    {/* Category & Tag Row */}
                    <div className="flex justify-between items-center text-[9px] font-mono font-bold tracking-wider w-full">
                      <span className="bg-[#793bfb]/20 text-[#c0a2ff] border border-[#793bfb]/30 px-2 py-0.5 rounded uppercase group-hover:bg-[#793bfb] group-hover:text-white transition-all duration-300">
                        Equipment
                      </span>
                      <span className="text-white/40 group-hover:text-white/60 transition-all font-mono text-[9px] uppercase tracking-wider">
                        Authorised Distributor
                      </span>
                    </div>

                    {/* Brand Logo Presentation Box */}
                    <div className="relative w-full h-24 bg-black backdrop-blur-xs rounded-xl shadow-inner border border-white/10 p-0 flex items-center justify-center transition-transform duration-500 group-hover:scale-[1.02] overflow-hidden">
                      {logoUrl && !logoErrors[brandObj.id] ? (
                        <img 
                          src={logoUrl} 
                          alt={name} 
                          loading="lazy"
                          decoding="async"
                          onError={() => setLogoErrors(prev => ({ ...prev, [brandObj.id]: true }))}
                          className="h-full w-full object-contain filter drop-shadow-xs transition-transform duration-500"
                        />
                      ) : (
                        <div className="flex flex-col items-center justify-center text-zinc-500 p-3 bg-zinc-50/50 w-full h-full select-none">
                          <Sparkles className="w-6 h-6 text-[#793bfb]/60 mb-1 animate-pulse" />
                          <span className="font-mono text-[8px] text-[#793bfb] font-black uppercase tracking-widest leading-none">CINESCOPE</span>
                          <span className="font-display text-[10px] text-zinc-800 font-extrabold uppercase tracking-tight mt-0.5">{name}</span>
                        </div>
                      )}
                      
                      {/* Subtle overlay gloss highlight */}
                      <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-out pointer-events-none" />
                    </div>

                    {/* Name and subheading */}
                    <div className="space-y-1">
                      <h3 className="font-display text-base font-black text-white group-hover:text-[#c0a2ff] transition-all duration-300 uppercase tracking-wide leading-none">
                        {name}
                      </h3>
                      <p className="font-sans text-xs font-semibold text-white/70 group-hover:text-white transition-all line-clamp-1">
                        {subheadingText2}
                      </p>
                    </div>
                  </div>

                  {/* Footer Arrow */}
                  <div className="pt-2 border-t border-white/5 flex items-center justify-between font-mono text-[9px] tracking-wider text-white/30 group-hover:text-[#c0a2ff] transition-all duration-300">
                    <span>EXPLORE SYSTEM</span>
                    <span className="transform group-hover:translate-x-1.5 transition-transform duration-300">↗</span>
                  </div>
                </div>
              );
            })}
          </div>
          ) : null}
        </div>
      </section>

      {/* Simulator Section - Temporarily Hidden (Cinescope Interactive Labs) */}
      {/* <section id="simulator-anchor" className="w-full bg-[#F4F7F9] border-y border-[#793bfb]/20 py-12 sm:py-20 lg:py-24 px-4 sm:px-6 flex flex-col items-center">
        <SimulatorLabs />
      </section> */}

      {/* Cinema Rig Labs Section - Temporarily Hidden */}
      {/* <section
        id="rig-labs-anchor"
        className="w-full py-20 px-4 sm:px-6 flex flex-col items-center bg-gradient-to-r from-[#2b2344] via-[#1a152e] to-[#2b2344] border-y border-white/10"
      >
        <div className="max-w-7xl w-full mb-10 text-center">
          <div className="inline-block bg-[#793bfb]/15 border border-[#793bfb]/25 px-3 py-1 rounded mb-4 font-mono text-[10px] tracking-widest uppercase text-[#793bfb]">
            Equipment Configurations
          </div>
          <h2 className="font-display text-4xl md:text-5xl font-black uppercase tracking-tight text-white leading-tight">
            Cinema Rigs <span className="text-[#793bfb]">Labs</span>
          </h2>
          <p className="font-sans text-sm text-white/60 max-w-lg mx-auto mt-4 leading-relaxed">
            Assemble professional cinema rigs, check payload balancing, power compatibility, and mount conflicts in real time.
          </p>
        </div>

        <div className="max-w-7xl w-full">
          <RigBuilder />
        </div>
      </section> */}

      {/* Below-the-fold dynamic landing sections with Suspense */}
      <Suspense fallback={null}>
        <DopCineTapeScroll />
        <FilmPosterTape />
        <div className="w-full max-w-7xl mx-auto px-6 pt-20 pb-20">
          <ProductionHousesMarquee />
        </div>
        <CinemaLabs />
        <TrustSection />
        <FAQSection />
        <AboutUsSection />
        <MilestoneSection />
        <ContactForm />
      </Suspense>
    </div>
  );
}

function AppContent() {
  const isOnline = useNetworkStatus();
  const setSiteSettings = useAppStore((state) => state.setSiteSettings);
  const siteSettings = useAppStore((state) => state.siteSettings);
  const [loadingMaintenance, setLoadingMaintenance] = useState(true);

  useEffect(() => {
    apiClient.get('/api/v1/auth/admin/settings/public')
      .then(res => {
        if (res.data) {
          setSiteSettings(res.data);
        }
      })
      .catch(err => {
        console.warn("Failed to check public system settings", err);
        setSiteSettings({});
      })
      .finally(() => {
        setLoadingMaintenance(false);
      });
  }, []);

  // Dynamically update site favicon on change
  useEffect(() => {
    if (siteSettings?.site_favicon) {
      const fullFaviconUrl = getImageUrl(siteSettings.site_favicon);
      // Update link tag if exists, otherwise create it
      let link = document.querySelector("link[rel*='icon']");
      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.getElementsByTagName('head')[0].appendChild(link);
      }
      link.href = fullFaviconUrl;
    }
  }, [siteSettings?.site_favicon]);

  if (!loadingMaintenance && siteSettings?.maintenance_mode) {
    const bgUrl = getImageUrl(siteSettings.maintenance_image);
    return (
      <div className="fixed inset-0 z-[99999] bg-[#090b11] flex items-center justify-center overflow-hidden font-sans">
        {/* Blurred backdrop image for ultimate high-end feel */}
        {siteSettings.maintenance_image && (
          <div 
            className="absolute inset-0 bg-cover bg-center scale-110 blur-2xl opacity-40 pointer-events-none"
            style={{ backgroundImage: `url(${bgUrl})` }}
          />
        )}
        
        {/* Subtle glassmorphic grid background */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#8080800a_1px,transparent_1px),linear-gradient(to_bottom,#8080800a_1px,transparent_1px)] bg-[size:14px_24px]" />
        
        {/* Main Content Card */}
        <div className="relative z-10 max-w-4xl w-full px-6 flex flex-col items-center justify-center text-center space-y-8 animate-fadeIn">
          {siteSettings.maintenance_image ? (
            <div className="w-full max-h-[70vh] flex items-center justify-center rounded-3xl overflow-hidden shadow-2xl border border-white/10 bg-black/30 backdrop-blur-md p-4">
              <img 
                src={bgUrl} 
                alt="System Maintenance" 
                className="max-w-full max-h-[65vh] object-contain rounded-2xl animate-pulseSlow"
              />
            </div>
          ) : (
            // Fallback maintenance UI if no image is uploaded
            <div className="bg-white/5 border border-white/10 backdrop-blur-md rounded-3xl p-8 sm:p-12 max-w-lg space-y-6 shadow-2xl">
              <div className="w-16 h-16 bg-[#793bfb]/10 border border-[#793bfb]/20 rounded-2xl flex items-center justify-center text-[#793bfb] mx-auto animate-pulse">
                <Sparkles size={32} />
              </div>
              <h1 className="font-display text-2xl sm:text-3xl text-white uppercase font-black tracking-tight">
                Scheduled Maintenance
              </h1>
              <p className="text-zinc-400 text-sm leading-relaxed">
                We are currently performing system upgrades to improve your cinema equipment configuration experience. We will be back online shortly.
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F4F7F9] text-zinc-800 flex flex-col justify-between font-sans selection:bg-[#793bfb]/10 selection:text-[#793bfb] overflow-x-clip relative">
      {!isOnline && (
        <div className="bg-gradient-to-r from-rose-600 to-red-600 text-white text-xs font-bold py-2 px-6 flex items-center justify-center gap-2 animate-pulse shrink-0 fixed top-0 w-full z-[9999] shadow-sm">
          <WifiOff size={14} />
          <span>Internet Disconnected. Technical inquiry & booking submissions are restricted.</span>
        </div>
      )}
      
      {/* Navigation Bar Header */}
      <Navbar />

      {/* Main Content Area */}
      <main className="flex-grow w-full pt-[80px] md:pt-[90px] flex flex-col">
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/contact" element={<ContactSpecialistPage />} />
            <Route path="/speak-with-an-expert" element={<ContactSpecialistPage />} />
            <Route path="/shop/*" element={<StoreRouter />} />
            <Route path="/knowledge" element={<KnowledgeList />} />
            <Route path="/knowledge/:slug" element={<KnowledgeDetail />} />
            <Route path="/scheduler" element={<SchedulerPage />} />
            <Route path="/cities/:slug" element={<CityDetail />} />
            <Route path="/newsletter/confirm" element={<NewsletterConfirmation />} />
            <Route path="/privacy" element={<LegalPageView slug="privacy" />} />
            <Route path="/terms" element={<LegalPageView slug="terms" />} />
            <Route path="/legal/:slug" element={<LegalPageView />} />
          </Routes>
          {/* Redesigned Premium Green Footer */}
          <Footer />
        </Suspense>
      </main>

      {/* Compare Overlay Panel & Lead Popup with Suspense */}
      <Suspense fallback={null}>
        <CompareOverlay />
        <LeadPopup />
      </Suspense>

      {/* Scroll To Top Button */}
      <ScrollToTopButton />

      {/* WhatsApp Sticky Float Button */}
      <WhatsAppButton />
    </div>
  );
}

function RouteMetadataManager() {
  const location = useLocation();

  useEffect(() => {
    const path = location.pathname;
    let title = "Cinescope India | Professional Cinema Equipment & Calibration";
    let desc = "Cinescope India - Premier destination for professional cinema gimbals, anamorphic lenses, stabilizer systems, and certified technical calibration services.";

    if (path === '/shop') {
      title = "Cinema Equipment Store | Cinescope India";
      desc = "Discover original cinema equipment directly from globally trusted partner brands with authorized warranty and local support.";
    } else if (path === '/shop/all') {
      title = "Cinema Equipment Catalog | Cinescope India";
      desc = "Browse complete catalog of professional cinema cameras, stabilizers, anamorphic lenses, and cinema rigs.";
    } else if (path.startsWith('/shop/brand/')) {
      const brandSlug = path.replace('/shop/brand/', '').replace(/\/$/, '');
      const brandName = brandSlug.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
      title = `${brandName} Cinema Systems & Equipment | Cinescope India`;
      desc = `Authorized sales, service, and warranty for ${brandName} cinema systems and camera gear in India.`;
    } else if (path.startsWith('/shop/')) {
      title = "Cinema Equipment Details | Cinescope India";
      desc = "Professional cinematography hardware specifications, technical manual, and direct quote requests.";
    } else if (path === '/contact' || path === '/speak-with-an-expert') {
      title = "Contact Us & Specialist Consultation | Cinescope India";
      desc = "Get in touch with Cinescope cinema engineers and specialists for tailored camera packages, rigging advice, and quotes.";
    } else if (path.startsWith('/knowledge')) {
      title = "Knowledge Center & Technical Guides | Cinescope India";
      desc = "Deep-dive technical guides, maintenance best practices, and calibration knowledge base for cinema professionals.";
    } else if (path === '/scheduler') {
      title = "Service Scheduler & Calibration | Cinescope India";
      desc = "Schedule certified maintenance, metrological alignment, and factory warranty repair for your cinema equipment.";
    } else if (path === '/privacy') {
      title = "Privacy Policy | Cinescope India";
      desc = "Privacy policy and data protection standards for Cinescope India customers and operators.";
    } else if (path === '/terms') {
      title = "Terms & Conditions | Cinescope India";
      desc = "Terms of service, warranty conditions, and equipment fulfillment agreements for Cinescope India.";
    }

    document.title = title;

    // Update Meta Description
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.name = 'description';
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = desc;

    // Update Canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = `https://cinescope.co.in${path === '/' ? '/' : path.replace(/\/$/, '')}`;

    // Update OpenGraph Title & Description
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) ogTitle.content = title;
    const ogDesc = document.querySelector('meta[property="og:description"]');
    if (ogDesc) ogDesc.content = desc;
    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) ogUrl.content = `https://cinescope.co.in${path}`;

    // Update Twitter Title & Description
    const twTitle = document.querySelector('meta[name="twitter:title"]');
    if (twTitle) twTitle.content = title;
    const twDesc = document.querySelector('meta[name="twitter:description"]');
    if (twDesc) twDesc.content = desc;
  }, [location.pathname]);

  return null;
}

function App() {
  return (
    <ToastProvider>
      <BrowserRouter>
        <RouteMetadataManager />
        <ScrollToAnchor />
        <AppContent />
      </BrowserRouter>
    </ToastProvider>
  );
}

export default App;
